import { computed, Injectable, PLATFORM_ID, signal, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import type { Session } from '@supabase/supabase-js';

import { environment } from '../../../environments/environment';
import { ActiveClinicService } from '../crm/active-clinic.service';
import { PermissionsService } from '../crm/permissions.service';
import { PlanAccessService } from '../crm/plan-access.service';
import { ToastService } from '../shared/toast.service';
import { SupabaseService } from './supabase.service';

/**
 * Which portal the admin app shows: 'superadmin' (platform role holder) or
 * 'admin' (clinic owner). Resolved server-side by public.admin_portal_for().
 */
export type AdminPortal = 'superadmin' | 'admin';

/** Response shape returned by the `login` Edge Function — see supabase/functions/login. */
interface LoginResponse {
  readonly error: string | null;
  readonly session: Session | null;
  readonly user: Record<string, unknown> | null;
  /** Absent from a `login` deployed before portals existed. */
  readonly portal?: AdminPortal | null;
}

function toAdminPortal(value: unknown): AdminPortal | null {
  return value === 'superadmin' || value === 'admin' ? value : null;
}

/**
 * The portal `login` chose, else the database's answer. `login` deployed before
 * portals existed sends none; asking the database then keeps this build working
 * with either version instead of signing everyone out.
 */
export async function pickAdminPortal(
  fromLogin: unknown,
  fetchFromDatabase: () => Promise<AdminPortal | null>,
): Promise<AdminPortal | null> {
  return toAdminPortal(fromLogin) ?? (await fetchFromDatabase());
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly supabase = inject(SupabaseService).client;
  private readonly permissions = inject(PermissionsService);
  private readonly activeClinic = inject(ActiveClinicService);
  private readonly planAccess = inject(PlanAccessService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  private readonly session = signal<Session | null>(null);

  /** Set right before a user-initiated signOut() so the onAuthStateChange handler
   *  below doesn't also treat it as an unexpected session expiry. */
  private manualSignOut = false;

  readonly currentUser = signal<any | null>(null);
  /** Admin app only — null on the CRM, and for a session not allowed into either portal. */
  readonly adminPortal = signal<AdminPortal | null>(null);
  readonly isAuthenticated = computed(() => this.session() !== null);
  readonly userEmail = computed(() => this.session()?.user.email ?? '');
  readonly userId = computed(() => this.session()?.user.id ?? null);

  constructor() {
    if (this.isBrowser) {
      this.supabase.auth.onAuthStateChange((event, session) => {
        const hadSession = this.session() !== null;
        this.session.set(session);
        if (!session) {
          this.currentUser.set(null);
          this.adminPortal.set(null);
        }
        if (event === 'SIGNED_OUT' && hadSession && !this.manualSignOut) {
          this.toast.error(this.translate.instant('common.sessionExpired'));
          this.router.navigateByUrl('/login');
        }
        this.manualSignOut = false;
      });
    }
  }

  /** Loads the current session, if any. Awaited from an app initializer before the router starts. */
  async init(): Promise<void> {
    if (!this.isBrowser) {
      return;
    }

    const { data } = await this.supabase.auth.getSession();
    this.session.set(data.session);

    const userId = data.session?.user?.id;
    if (!userId) {
      return;
    }

    // Must run for any valid session regardless of the profile queries below —
    // crmAccessGuard checks permissions.hasAnyPermission() on every route
    // activation (including right after this runs on a page refresh), so
    // gating this behind a richer query succeeding was sending users with a
    // perfectly valid session to /no-access whenever that query came back
    // empty (same root cause as the login() Edge Function bug — see
    // supabase/functions/login).
    await this.permissions.refresh();

    if (environment.platform === 'admin') {
      this.adminPortal.set(await this.fetchAdminPortal());
    }

    // Flat, embed-free columns only — this must not depend on the
    // branches_tb/companies_tb join below succeeding, since shell.ts's
    // profile header (name/photo) only ever needs these.
    const { data: profile } = await this.supabase
      .from('users_tb')
      .select('id, name, photo, is_superAdmin, access_crm, branches_id, company_id')
      .eq('user_uuid', userId)
      .maybeSingle();

    this.currentUser.set(profile ?? null);
    await this.activeClinic.refresh((profile as any)?.branches_id ?? null);

    // CRM portal only: whether the clinic has an active plan (drives the /no-plan gate).
    if (environment.platform === 'crm' && !(profile as any)?.is_superAdmin) {
      await this.planAccess.refresh();
    }

    // Best-effort enrichment (clinic/company name for shell.ts's header) —
    // never blocks anything above if it fails; merged in when it succeeds.
    if (profile && environment.platform === 'crm') {
      const { data: withBranch } = await this.supabase
        .from('users_tb')
        .select('branches_tb ( name, companies_tb ( name, corporate_reason ) )')
        .eq('user_uuid', userId)
        .maybeSingle();
      if (withBranch) {
        this.currentUser.set({ ...profile, ...withBranch });
      }
    }
  }

  /**
   * Delegates the whole login decision to the `login` Edge Function
   * (supabase/functions/login) instead of doing signInWithPassword() +
   * post-hoc users_tb check + signOut-on-failure here. Both platforms share
   * one Supabase Auth backend, so the app-specific check ("is THIS account
   * allowed into THIS platform") can't live in the Custom Access Token Hook
   * — its payload has no way to know which frontend a login is for. The
   * Edge Function does that check server-side and only ever returns a
   * session if it matches; an unauthorized account for the requested
   * platform never reaches this client with a usable token at all.
   */
  async login(email: string, password: string): Promise<{ error: string | null }> {
    const { data, error } = await this.supabase.functions.invoke<LoginResponse>('login', {
      body: { email, password, platform: environment.platform },
    });

    if (error || !data) {
      return { error: error?.message ?? 'Unauthorized access' };
    }

    if (data.error || !data.session || !data.user) {
      return { error: data.error ?? 'Unauthorized access' };
    }

    const { error: setSessionError } = await this.supabase.auth.setSession({
      access_token: data.session.access_token,
      refresh_token: data.session.refresh_token,
    });

    if (setSessionError) {
      return { error: setSessionError.message };
    }

    this.session.set(data.session);
    this.currentUser.set(data.user);
    this.adminPortal.set(await this.resolveAdminPortal(data.portal));
    await this.permissions.refresh();
    await this.activeClinic.refresh((data.user as any).branches_id ?? null);
    if (environment.platform === 'crm' && !(data.user as any).is_superAdmin) {
      await this.planAccess.refresh();
    }

    return { error: null };
  }

  /** Requires an active session (always true here — this is only ever called from the logged-in shell). */
  async changePassword(newPassword: string): Promise<{ error: string | null }> {
    const { error } = await this.supabase.auth.updateUser({ password: newPassword });
    return { error: error?.message ?? null };
  }

  /**
   * Self-service "forgot password" — emails a recovery link pointing back at /reset-password.
   * That page relies on SupabaseService's `detectSessionInUrl: true` to turn the link's
   * `#access_token=...&type=recovery` hash into a temporary session, then calls
   * changePassword() above to actually set the new password.
   */
  async resetPassword(email: string): Promise<{ error: string | null }> {
    const { error } = await this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error?.message ?? null };
  }

  private async resolveAdminPortal(fromLogin: unknown): Promise<AdminPortal | null> {
    if (environment.platform !== 'admin') return null;
    return pickAdminPortal(fromLogin, () => this.fetchAdminPortal());
  }

  private async fetchAdminPortal(): Promise<AdminPortal | null> {
    const { data, error } = await this.supabase.rpc('my_admin_portal');
    return error ? null : toAdminPortal(data);
  }

  async logout(): Promise<void> {
    this.manualSignOut = true;
    await this.supabase.auth.signOut();
    this.session.set(null);
    this.currentUser.set(null);
    this.adminPortal.set(null);
  }

  private currentRouteTitle(): string {
    return typeof document !== 'undefined' ? document.title : '';
  }
}
